import { aiService } from './aiService';

/**
 * ADVANCED MARKETPLACE COPYWRITING SERVICE
 * 
 * Generates SEO-optimized product content following detailed marketplace rules
 * for Amazon, Mercado Livre, Magalu, and Shopee.
 * 
 * Features:
 * - Strategic product naming (max 120 chars)
 * - Kit naming optimization
 * - Professional persuasive descriptions (120-250 words)
 * - Differentials list
 * - Technical specifications (separate from commercial text)
 */

/**
 * Generate advanced marketplace copywriting content
 * @param {Object} productData - Product or kit data with CSV information
 * @param {Object} options - Generation options
 * @returns {Promise<Object>} Structured copywriting content
 */
export async function generateMarketplaceCopywriting(productData, options = {}) {
  try {
    const isKit = productData?.productType === 'kit';
    
    // Build comprehensive prompt with detailed copywriting rules
    let prompt = buildCopywritingPrompt(productData, isKit);
    
    const response = await aiService?.generateContent(prompt, {
      maxTokens: 1500,
      responseFormat: 'json'
    });
    
    if (response?.success) {
      let content;
      try {
        content = JSON.parse(response?.content);
      } catch {
        throw new Error('Resposta da IA inválida');
      }
      
      return {
        success: true,
        content: {
          productName: content?.productName || '',
          kitName: content?.kitName || '',
          description: content?.description || '',
          differentials: content?.differentials || [],
          technicalSpecs: content?.technicalSpecs || {},
        }
      };
    } else {
      throw new Error(response?.error || 'IA não disponível');
    }
  } catch (error) {
    console.error('Error generating marketplace copywriting:', error);
    return {
      success: false,
      error: error?.message || 'Erro ao gerar conteúdo'
    };
  }
}

/**
 * Build detailed copywriting prompt following marketplace optimization rules
 */
function buildCopywritingPrompt(productData, isKit) {
  let prompt = `Você é um especialista em copywriting estratégico para grandes marketplaces como Amazon, Mercado Livre, Magalu e Shopee, com foco em SEO interno, conversão e posicionamento competitivo.

Sua tarefa é gerar:

1) Nome do Produto otimizado para SEO e conversão
2) Nome otimizado caso seja um kit
3) Descrição profissional persuasiva
4) Lista de diferenciais
5) Especificações técnicas organizadas separadamente

==================================================================== 
REGRAS GERAIS OBRIGATÓRIAS
====================================================================

- NÃO incluir EAN, NCM ou códigos internos na descrição comercial.
- NÃO repetir o nome do produto de forma forçada.
- NÃO usar frases genéricas como "solução prática", "ideal para você" ou "alta qualidade" sem contexto técnico.
- NÃO inventar informações que não estejam nos dados fornecidos.
- NÃO escrever de forma robótica.
- NÃO mencionar ausência de informações.
- NÃO misturar dados técnicos com texto comercial.
- Escrever como grandes sellers que lideram ranking nos marketplaces.
- Foco em utilidade real, aplicação prática e decisão de compra.

==================================================================== 
ESTRUTURA OBRIGATÓRIA DA RESPOSTA
====================================================================

========================
1) NOME DO PRODUTO
========================

- Máximo 120 caracteres.
- Começar com o tipo do produto.
- Incluir principal palavra-chave de busca.
- Incluir quantidade, tamanho ou característica relevante.
- Seguir padrão estratégico:
  Estrutura recomendada: Tipo + Marca + Quantidade + Aplicação + Diferencial Técnico
- O nome deve ser pensado para ranqueamento interno em marketplace.

========================
2) NOME DO KIT (se aplicável)
========================

- Nome estratégico com foco em benefício e aplicação.
- Clareza sobre composição.
- Pode incluir público ou contexto de uso.
- Não ultrapassar 120 caracteres.

========================
3) DESCRIÇÃO PROFISSIONAL
========================

- Entre 120 e 250 palavras.
- Primeiro parágrafo: benefício principal e aplicação direta.
- Segundo parágrafo: diferenciais técnicos reais.
- Terceiro parágrafo: contexto de uso (profissional, doméstico, oficina, indústria, manutenção etc).
- Linguagem natural, clara, estratégica e persuasiva.
- Foco em utilidade prática e valor percebido.

========================
4) DIFERENCIAIS
========================

- Lista objetiva em bullet points com benefícios claros e específicos.
- Evitar frases vagas. Cada ponto deve agregar valor real.

========================
5) ESPECIFICAÇÕES TÉCNICAS
========================

- Organizadas separadamente do texto comercial.
- Incluir apenas dados fornecidos como:
  * Peso
  * Dimensões
  * Quantidade de peças
  * Material
  * Marca
  * Categoria
- Nunca misturar especificações dentro da narrativa comercial.

==================================================================== 
OTIMIZAÇÃO EXTRA (ESTRATÉGIA GRANDES PLAYERS)
====================================================================

- Usar palavras-chave naturais dentro do texto, sem parecer forçado.
- Estruturar texto pensando em escaneabilidade.
- Gerar nome com lógica de busca real.
- Priorizar clareza e aplicação prática.
- Se for kit, estruturar para aumentar ticket médio.

`;

  // Add product/kit specific data
  if (isKit) {
    prompt += `\n==================================================================== \nINFORMAÇÕES DO KIT\n====================================================================\n\n`;
    prompt += `Nome do Kit: ${productData?.name}\n`;
    prompt += `Marca: ${productData?.brand || 'N/A'}\n`;
    prompt += `Categoria: ${productData?.category || 'N/A'}\n`;
    
    if (productData?.metadata?.kitComponents && Array.isArray(productData?.metadata?.kitComponents)) {
      prompt += `\nProdutos inclusos no kit (${productData?.metadata?.kitComponents?.length} itens):\n`;
      
      productData?.metadata?.kitComponents?.forEach((comp, index) => {
        prompt += `\n=== PRODUTO ${index + 1} ===\n`;
        prompt += `Nome: ${comp?.name || 'N/A'}\n`;
        prompt += `SKU: ${comp?.sku || 'N/A'}\n`;
        prompt += `Marca: ${comp?.brand || 'N/A'}\n`;
        prompt += `Categoria: ${comp?.category || 'N/A'}\n`;
        
        if (comp?.csvOriginalData) {
          const csvData = comp?.csvOriginalData;
          if (csvData?.especificacoes) prompt += `Especificações: ${csvData?.especificacoes}\n`;
          if (csvData?.material) prompt += `Material: ${csvData?.material}\n`;
          if (csvData?.dimensoes) prompt += `Dimensões: ${csvData?.dimensoes}\n`;
          if (csvData?.peso) prompt += `Peso: ${csvData?.peso}\n`;
          if (csvData?.cor) prompt += `Cor: ${csvData?.cor}\n`;
          if (csvData?.voltagem) prompt += `Voltagem: ${csvData?.voltagem}\n`;
          if (csvData?.compatibilidade) prompt += `Compatibilidade: ${csvData?.compatibilidade}\n`;
          if (csvData?.garantia) prompt += `Garantia: ${csvData?.garantia}\n`;
          if (csvData?.aplicacao) prompt += `Aplicação: ${csvData?.aplicacao}\n`;
        }
        
        if (comp?.weight) prompt += `Peso: ${comp?.weight}kg\n`;
        if (comp?.width && comp?.height && comp?.length) {
          prompt += `Dimensões: ${comp?.width}x${comp?.height}x${comp?.length}cm\n`;
        }
      });
    }
  } else {
    prompt += `\n==================================================================== \nINFORMAÇÕES DO PRODUTO\n====================================================================\n\n`;
    prompt += `Nome: ${productData?.name}\n`;
    prompt += `SKU: ${productData?.sku || 'N/A'}\n`;
    prompt += `Marca: ${productData?.brand || 'N/A'}\n`;
    prompt += `Categoria: ${productData?.category || 'N/A'}\n`;
    
    if (productData?.csvOriginalData) {
      const csvData = productData?.csvOriginalData;
      prompt += `\n=== DADOS DO CSV (USE APENAS ESTES DADOS) ===\n`;
      if (csvData?.especificacoes) prompt += `Especificações Técnicas: ${csvData?.especificacoes}\n`;
      if (csvData?.material) prompt += `Material: ${csvData?.material}\n`;
      if (csvData?.dimensoes) prompt += `Dimensões: ${csvData?.dimensoes}\n`;
      if (csvData?.peso) prompt += `Peso: ${csvData?.peso}\n`;
      if (csvData?.cor) prompt += `Cor: ${csvData?.cor}\n`;
      if (csvData?.voltagem) prompt += `Voltagem: ${csvData?.voltagem}\n`;
      if (csvData?.compatibilidade) prompt += `Compatibilidade: ${csvData?.compatibilidade}\n`;
      if (csvData?.garantia) prompt += `Garantia: ${csvData?.garantia}\n`;
      if (csvData?.aplicacao) prompt += `Aplicação: ${csvData?.aplicacao}\n`;
      if (csvData?.conteudo_embalagem) prompt += `Conteúdo da Embalagem: ${csvData?.conteudo_embalagem}\n`;
    }
    
    if (productData?.weight) prompt += `Peso: ${productData?.weight}kg\n`;
    if (productData?.width && productData?.height && productData?.length) {
      prompt += `Dimensões: ${productData?.width}x${productData?.height}x${productData?.length}cm\n`;
    }
    if (productData?.ean) prompt += `EAN: ${productData?.ean}\n`;
    if (productData?.ncm) prompt += `NCM: ${productData?.ncm}\n`;
  }

  // Add response format
  prompt += `\n==================================================================== \nFORMATO DE RESPOSTA\n====================================================================\n\n`;
  prompt += `Retorne a resposta organizada exatamente nas seções definidas acima. Não use emojis. Não use markdown. Não inclua explicações extras. Não retorne JSON. Não inclua comentários fora da estrutura.\n\n`;
  prompt += `Retorne APENAS um JSON válido no formato:\n\n`;
  prompt += `{\n`;
  prompt += `  "productName": "nome otimizado do produto (max 120 chars)",\n`;
  prompt += `  "kitName": "nome otimizado do kit se aplicável (max 120 chars) ou string vazia",\n`;
  prompt += `  "description": "descrição profissional persuasiva entre 120-250 palavras",\n`;
  prompt += `  "differentials": ["diferencial 1", "diferencial 2", "diferencial 3", "diferencial 4"],\n`;
  prompt += `  "technicalSpecs": {\n`;
  prompt += `    "peso": "valor",\n`;
  prompt += `    "dimensoes": "valor",\n`;
  prompt += `    "material": "valor",\n`;
  prompt += `    "marca": "valor",\n`;
  prompt += `    "categoria": "valor"\n`;
  prompt += `  }\n`;
  prompt += `}\n\n`;
  prompt += `CRÍTICO: Use SOMENTE as informações fornecidas. Não adicione dados genéricos ou inventados.`;

  return prompt;
}

export default {
  generateMarketplaceCopywriting
};